import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import {
  Modal,
  InputNumber,
  DatePicker,
  Form,
  Button,
  Select,
  Switch,
  Input,
} from 'antd';

import {
  createSession,
  RISK_APPROACHES,
  CASH_APPROACH,
  RISK_WEIGHTED_APPROACH,
  TRADING_TIME_TYPES,
  SCENARIO_TYPES,
  HISTORICAL_SCENARIO,
  REAL_TIME_SCENARIO,
  REPORTING_CURRENCIES,
  selectors,
  LIMIT_METRIC_CHOICES,
  MANAGEMENT_TYPES,
  PORTFOLIO_MANAGEMENT,
  ReplaySessionDefaults,
} from 'state';
import { PercentInput } from 'components';
import { workdayCount } from '../../state/app/utils';

type props = {
  visible: boolean;
  hide: () => void;
  klassId: number;
  replayDefaults?: ReplaySessionDefaults;
  replaySourceSessionId?: number;
};

const getNow = () => dayjs().startOf('day');

const initialValues = {
  scenarioType: 0,
  riskApproach: 0,
  managementType: PORTFOLIO_MANAGEMENT,

  limitOrderCollateral: 10,
  riskWeightedLimit: 100_000_000,
  transactionFee: 0,
  short_sell_leverage: 30,
  fxLeverage: 100,
  bondLeverage: 100,
  initialAmount: 100_000_000,
  reportingCurrency: 'USD',
  allowShortSell: true,
  clientMoney: false,
  showAuctions: false,
  metricsInterval: 24,
  tradingTimeType: 0,
  dates: [getNow().subtract(1, 'months'), getNow()] as [Dayjs, Dayjs],
  newsImportances: '1,2',
  frequency: 1,
  passTime: 180,
  delay: 0,
  clientMoneyScenario: null,
  perSectorAlpha: false,
};

const toDayjsRange = (
  startDate: string | null,
  endDate: string | null
): [Dayjs, Dayjs] | undefined => {
  if (!startDate || !endDate) {
    return undefined;
  }

  return [dayjs(startDate), dayjs(endDate)];
};

// Replay intentionally mirrors only the fields supported by this create modal.
const mapReplayDefaultsToFormValues = (defaults: ReplaySessionDefaults) => ({
  scenarioType: defaults.scenario_type,
  riskApproach: defaults.risk_approach,
  managementType: defaults.management_type,
  limitOrderCollateral: defaults.limit_order_collateral * 100,
  riskWeightedLimit: defaults.risk_weighted_limit,
  short_sell_leverage: defaults.short_sell_leverage * 100,
  fxLeverage: defaults.fx_leverage * 100,
  bondLeverage: defaults.bond_leverage * 100,
  initialAmount: defaults.starting_cash_amount,
  reportingCurrency: defaults.reporting_currency,
  allowShortSell: defaults.allow_short_sell,
  clientMoney: defaults.client_money,
  showAuctions: defaults.show_auctions,
  tradingTimeType: defaults.trading_time_type,
  dates: (toDayjsRange(defaults.start_date, defaults.end_date) ||
    initialValues.dates) as [Dayjs, Dayjs],
  newsImportances: defaults.news_importances.join(','),
  frequency: defaults.frequency,
  delay: defaults.delay_minutes_left,
  clientMoneyScenario: defaults.client_money_scenario,
  passTime: defaults.historical_sleep_time,
  perSectorAlpha: false,
});

const SessionSettings: React.FC<props> = ({
  visible,
  hide,
  klassId,
  replayDefaults,
  replaySourceSessionId,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const createSessionWaiting = useSelector(selectors.createSessionWaiting);
  const createSessionSucceeded = useSelector(selectors.createSessionSucceeded);
  const createSessionFailed = useSelector(selectors.createSessionFailed);

  const [riskApproach, setRiskApproach] = useState(initialValues.riskApproach);
  // const [limitMetric, setLimitMetric] = useState<number | undefined>(undefined);
  const [scenarioType, setScenarioType] = useState(initialValues.scenarioType);
  // console.log('limitMetric: ', limitMetric);
  const [sessionDuration, setSessionDuration] = useState('-');
  const [submitted, setSubmitted] = useState(false);
  // const historicalStep = useSelector(selectors.historicalStep);
  const scenarios = Object.values(
    useSelector(selectors.ClientDealingScenarios)
  );
  // console.log(scenarios);

  const computeDurationFromValues = (
    formDates?: [Dayjs, Dayjs],
    formFrequency?: number,
    passTime?: number
  ) => {
    let sessionSeconds = 0;
    const periodDuration = passTime || 0;
    if (!formFrequency || typeof formFrequency !== 'number') {
      setSessionDuration('-');
      return;
    }

    if (formDates?.length === 2) {
      const workdays = workdayCount(formDates[0], formDates[1], formFrequency);
      sessionSeconds = workdays * periodDuration;
    }

    const sessionHours = Math.floor(sessionSeconds / 60 / 60);
    const sessionMinutes = Math.floor((sessionSeconds / 60) % 60);
    setSessionDuration(`${sessionHours}h:${sessionMinutes}m`);
  };

  const computeDuration = () => {
    computeDurationFromValues(
      form.getFieldValue('dates'),
      form.getFieldValue('frequency'),
      form.getFieldValue('passTime')
    );
  };

  useEffect(() => {
    if (!visible) {
      setSubmitted(false);
      return;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (replayDefaults) {
      const replayValues = mapReplayDefaultsToFormValues(replayDefaults);
      form.setFieldsValue(replayValues);
      setRiskApproach(replayValues.riskApproach);
      setScenarioType(replayValues.scenarioType);
      computeDurationFromValues(
        replayValues.dates,
        replayValues.frequency,
        replayValues.passTime
      );
      return;
    }

    form.resetFields();
    setRiskApproach(initialValues.riskApproach);
    setScenarioType(initialValues.scenarioType);
    computeDurationFromValues(
      initialValues.dates,
      initialValues.frequency,
      initialValues.passTime
    );
  }, [visible, replayDefaults]);

  useEffect(() => {
    if (!submitted) {
      return;
    }

    if (createSessionSucceeded) {
      setSubmitted(false);
      hide();
      return;
    }

    if (createSessionFailed) {
      setSubmitted(false);
    }
  }, [submitted, createSessionSucceeded, createSessionFailed, hide]);

  return (
    <Modal
      open={visible}
      onCancel={() => {
        if (!createSessionWaiting) {
          hide();
        }
      }}
      closable={!createSessionWaiting}
      maskClosable={!createSessionWaiting}
      keyboard={!createSessionWaiting}
      title={
        replayDefaults && replaySourceSessionId
          ? `Replay Session ${replaySourceSessionId}`
          : 'Create Session'
      }
      footer={[
        <Button
          type="primary"
          key="submit"
          loading={createSessionWaiting}
          disabled={createSessionWaiting}
          onClick={() => form.submit()}
        >
          Create
        </Button>,
      ]}
    >
      <Form
        form={form}
        initialValues={initialValues}
        labelCol={{ span: 8 }}
        onValuesChange={computeDuration}
        onFinish={() => {
          if (createSessionWaiting) {
            return;
          }

          const {
            scenarioType,
            limitOrderCollateral,
            initialAmount,
            metricsInterval,
            dates,
            short_sell_leverage,
            fxLeverage,
            bondLeverage,
            riskWeightedLimit,
            riskApproach,
            managementType,
            reportingCurrency,
            tradingTimeType,
            allowShortSell,
            newsImportances,
            frequency,
            delay,
            clientMoney,
            clientMoneyScenario,
            showAuctions,
            limitMetric,
            passTime,
            perSectorAlpha,
          } = form.getFieldsValue();

          console.log(clientMoneyScenario);
          setSubmitted(true);

          dispatch(
            createSession({
              klass_id: klassId,
              scenario_type: scenarioType,
              limit_order_collateral:
                (limitOrderCollateral || initialValues.limitOrderCollateral) /
                100,
              transaction_fee: 0,
              short_sell_leverage:
                (short_sell_leverage ?? initialValues.short_sell_leverage) /
                100,
              fx_leverage: (fxLeverage || initialValues.fxLeverage) / 100,
              bond_leverage: (bondLeverage || initialValues.bondLeverage) / 100,
              starting_cash_amount:
                initialAmount || initialValues.initialAmount,
              reporting_currency:
                reportingCurrency || initialValues.reportingCurrency,
              metrics_interval: metricsInterval,
              start_date: (dates || [])[0]?.format(
                'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'
              ),
              end_date: (dates || [])[1]?.format(
                'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'
              ),
              risk_weighted_limit:
                riskWeightedLimit || initialValues.riskWeightedLimit,
              risk_approach: riskApproach,
              management_type: managementType,
              trading_time_type: tradingTimeType,
              spread_widener: 0,
              allow_short_sell: allowShortSell,
              client_money: clientMoney || initialValues.clientMoney,
              show_auctions: showAuctions || initialValues.showAuctions,
              news_importances:
                newsImportances?.replaceAll(' ', '').split(',') || [],
              frequency: frequency || initialValues.frequency,
              delay_minutes_left: delay || initialValues.delay,
              limit_metric: limitMetric,
              client_money_scenario: clientMoneyScenario,
              historical_sleep_time: passTime,
              per_sector_alpha:
                scenarioType === HISTORICAL_SCENARIO
                  ? Boolean(perSectorAlpha)
                  : false,
              replay_source_session_id: replaySourceSessionId,
            })
          );
          // hide();
        }}
      >
        <Form.Item
          name="scenarioType"
          label="Scenario Type"
          rules={[{ required: true }]}
        >
          <Select
            onChange={(a: number) => {
              setScenarioType(a);
              if (a !== HISTORICAL_SCENARIO) {
                form.setFieldsValue({ perSectorAlpha: false });
              }
            }}
            style={{ width: 200 }}
          >
            <Select.Option value={0}>{SCENARIO_TYPES[0]}</Select.Option>
            <Select.Option value={1}>{SCENARIO_TYPES[1]}</Select.Option>
          </Select>
        </Form.Item>
        {scenarioType === HISTORICAL_SCENARIO && (
          <>
            <span style={{ paddingLeft: 157 }}>
              Session duration: {sessionDuration}
            </span>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.clientMoney !== curr.clientMoney ||
                prev.clientMoneyScenario !== curr.clientMoneyScenario
              }
            >
              {({ getFieldValue, setFieldsValue }) => {
                // const enabled = getFieldValue('clientMoney');

                const clientMoneyOn = getFieldValue('clientMoney');
                const scenarioId = getFieldValue('clientMoneyScenario');

                // as soon as the user has clientMoney=true and picks a scenario
                if (clientMoneyOn && scenarioId != null) {
                  const sel = scenarios.find(s => s.id === scenarioId);
                  if (sel) {
                    console.log(sel.first_date_in_session);
                    console.log(sel.session_end_date);

                    setFieldsValue({
                      // assuming your form fields are named "dates" and "initialAmount"
                      dates: [
                        dayjs(sel.first_date_in_session, 'YYYY-MM-DD'),
                        dayjs(sel.session_end_date, 'YYYY-MM-DD'),
                      ],
                      initialAmount: sel.aum,
                      frequency: sel.period_frequency,
                    });
                    computeDuration();
                  }
                }

                return (
                  <>
                    <Form.Item
                      name="dates"
                      label="Historical Period"
                      rules={[{ required: true }]}
                    >
                      <DatePicker.RangePicker
                        format="YYYY-MM-DD"
                        disabled={clientMoneyOn}
                      />
                    </Form.Item>
                    <Form.Item
                      name="frequency"
                      label="Frequency (Days)"
                      rules={[
                        { required: true },
                        {
                          type: 'number',
                          message: 'Please input a valid number!',
                        },
                      ]}
                    >
                      <InputNumber min={1} max={31} disabled={clientMoneyOn} />
                    </Form.Item>
                    {/*<Form.Item*/}
                    {/*  name="riskApproach"*/}
                    {/*  label="Risk approach"*/}
                    {/*  rules={[{ required: true }]}*/}
                    {/*>*/}
                    {/*  <Select*/}
                    {/*    onChange={(a: number) => setRiskApproach(a)}*/}
                    {/*    style={{ width: 200 }}*/}
                    {/*  >*/}
                    {/*    <Select.Option value={0}>{RISK_APPROACHES[0]}</Select.Option>*/}
                    {/*    <Select.Option value={1}>{RISK_APPROACHES[1]}</Select.Option>*/}
                    {/*  </Select>*/}
                    {/*</Form.Item>*/}
                    <Form.Item
                      name="passTime"
                      label="Period Duration"
                      rules={[
                        { required: true },
                        {
                          type: 'number',
                          message: 'Please input a valid number!',
                        },
                      ]}
                    >
                      <InputNumber min={20} max={2000} />
                    </Form.Item>
                    <Form.Item
                      valuePropName="checked"
                      name="perSectorAlpha"
                      label="Per-Sector Alpha"
                    >
                      <Switch />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>
            <Form.Item
              name="delay"
              label="Delay (mins.)"
              rules={[
                { required: true },
                { type: 'number', message: 'Please input a valid number!' },
              ]}
            >
              <InputNumber min={0} max={31} />
            </Form.Item>
            <Form.Item
              valuePropName="checked"
              name="clientMoney"
              label="Client Dealing"
              rules={[{ required: true }]}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.clientMoney !== curr.clientMoney ||
                prev.clientMoneyScenario !== curr.clientMoneyScenario
              }
            >
              {({ getFieldValue }) => {
                const clientMoneyScenario_ = getFieldValue(
                  'clientMoneyScenario'
                );

                console.log(clientMoneyScenario_);

                return getFieldValue('clientMoney') ? (
                  <Form.Item
                    name="clientMoneyScenario"
                    label="clientMoneyScenario"
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Select a scenario"
                      style={{ width: 300 }}
                    >
                      {scenarios.map(s => (
                        <Select.Option key={s.id} value={s.id}>
                          {s.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
            <Form.Item
              valuePropName="checked"
              name="showAuctions"
              label="Auctions"
              rules={[{ required: true }]}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="newsImportances"
              label="News"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </>
        )}
        {scenarioType === REAL_TIME_SCENARIO && (
          <Form.Item
            name="tradingTimeType"
            label="Trading Time"
            rules={[{ required: true }]}
          >
            <Select style={{ width: 200 }}>
              <Select.Option value={0}>{TRADING_TIME_TYPES[0]}</Select.Option>
              <Select.Option value={1}>{TRADING_TIME_TYPES[1]}</Select.Option>
            </Select>
          </Form.Item>
        )}
        <Form.Item
          name="riskApproach"
          label="Risk approach"
          rules={[{ required: true }]}
        >
          <Select
            onChange={(a: number) => setRiskApproach(a)}
            style={{ width: 200 }}
          >
            <Select.Option value={0}>{RISK_APPROACHES[0]}</Select.Option>
            <Select.Option value={1}>{RISK_APPROACHES[1]}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="managementType" // Wealth management or Portfolio management
          label="Role"
          rules={[{ required: true }]}
        >
          <Select
            // onChange={(a: number) => setRiskApproach(a)}
            style={{ width: 200 }}
          >
            <Select.Option value={0}>{MANAGEMENT_TYPES[0]}</Select.Option>
            <Select.Option value={1}>{MANAGEMENT_TYPES[1]}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="reportingCurrency"
          label="Reporting currency"
          rules={[{ required: true }]}
        >
          <Select style={{ width: 200 }}>
            {REPORTING_CURRENCIES.map(ccy => {
              return (
                <Select.Option value={ccy} key={ccy}>
                  {ccy}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>

        {riskApproach === CASH_APPROACH && (
          <>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.clientMoney !== curr.clientMoney
              }
            >
              {({ getFieldValue }) => {
                const enabled = getFieldValue('clientMoney');
                return (
                  <>
                    <Form.Item
                      name="initialAmount"
                      label="Initial Amount"
                      rules={[{ required: true }]}
                    >
                      <InputNumber
                        disabled={enabled}
                        formatter={value =>
                          ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value =>
                          Number(value?.replace(/\$\s?|(,*)/g, '') || 0)
                        }
                        style={{ width: 130 }}
                      />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>

            {/*<Form.Item*/}
            {/*  name="initialAmount"*/}
            {/*  label="Initial Amount"*/}
            {/*  rules={[{ required: true }]}*/}
            {/*>*/}
            {/*  <InputNumber*/}
            {/*    formatter={value =>*/}
            {/*      ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')*/}
            {/*    }*/}
            {/*    parser={value => `${value?.replace(/\$\s?|(,*)/g, '')}`}*/}
            {/*    style={{ width: 130 }}*/}
            {/*  />*/}
            {/*</Form.Item>*/}
            <Form.Item
              name="limitOrderCollateral"
              label="Limit Order Collateral"
              rules={[{ required: true }]}
            >
              <PercentInput />
            </Form.Item>
            <Form.Item
              name="short_sell_leverage"
              label="Short selling limit"
              rules={[{ required: true }]}
            >
              <PercentInput min={0} max={100} />
            </Form.Item>
            <Form.Item
              name="fxLeverage"
              label="FX Leverage"
              rules={[{ required: true }]}
            >
              <PercentInput />
            </Form.Item>
            <Form.Item
              name="bondLeverage"
              label="Bond Leverage"
              rules={[{ required: true }]}
            >
              <PercentInput />
            </Form.Item>
          </>
        )}
        {riskApproach === RISK_WEIGHTED_APPROACH && (
          <>
            <Form.Item
              name="riskWeightedLimit"
              label="Risk weighted limit"
              rules={[{ required: true }]}
            >
              <InputNumber
                formatter={value =>
                  ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value =>
                  Number(value?.replace(/\$\s?|(,*)/g, '') || 0)
                }
                style={{ width: 130 }}
              />
            </Form.Item>
          </>
        )}
        <Form.Item
          valuePropName="checked"
          name="allowShortSell"
          label="Allow Short Sell"
          rules={[{ required: true }]}
        >
          <Switch />
        </Form.Item>
        {/*<Form.Item*/}
        {/*  valuePropName="checked"*/}
        {/*  name="limitMetric"*/}
        {/*  label="Limit metric"*/}
        {/*  // rules={[{ required: true }]}*/}
        {/*>*/}
        {/*  <Select*/}
        {/*    style={{ width: 200 }}*/}
        {/*    onChange={(a: number) => setLimitMetric(a)}*/}
        {/*    allowClear*/}
        {/*  >*/}
        {/*    <Select.Option value={0}>{LIMIT_METRIC_CHOICES[0]}</Select.Option>*/}
        {/*    <Select.Option value={1}>{LIMIT_METRIC_CHOICES[1]}</Select.Option>*/}
        {/*    <Select.Option value={2}>{LIMIT_METRIC_CHOICES[2]}</Select.Option>*/}
        {/*    <Select.Option value={3}>{LIMIT_METRIC_CHOICES[3]}</Select.Option>*/}
        {/*    <Select.Option value={4}>{LIMIT_METRIC_CHOICES[4]}</Select.Option>*/}
        {/*  </Select>*/}
        {/*</Form.Item>*/}
      </Form>
    </Modal>
  );
};

export default SessionSettings;
